import Cancel from '@mui/icons-material/Cancel'
import CheckCircle from '@mui/icons-material/CheckCircle'
import Checkbox from '@mui/material/Checkbox'
import debounce from 'lodash/debounce'
import moment from 'moment'
import { useCallback, useContext, useEffect, useState } from 'react'
import { getTimezoneOffset } from 'src/consumer/helpers/utilFunctions'
import { Spinner } from 'react-bootstrap'
import { toast } from 'react-toastify'
import ColorLinesIllustration from 'src/assets/color_lines_illustration.svg'
import {
  APP_CONSTANTS, DISPLAY_NAMES_OF_WEEKDAYS,
  EVENT_TEMPLATE_DATA, NEW_MENTOR_SCHEDULE,
  RENDERING_ORDER_OF_DAYS, timezones_list
} from 'src/consumer/constants'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import { GenericDisplayFlex, GenericDisplayFlexColumn } from 'src/mentor/layout/Styles'
import {
  getMPData,
  getMPDataWithoutParams,
  postMPData,
  postMPDataWithoutParams,
} from 'src/mentor/services'
import { addSlotTimeToData } from '../MentorAvailabilityPage/Components'
import {
  GenericModalHeadlineLevelTwo,
  ModalDaysContainer,
  getSlotTimeOptions,
  getTimeZoneOptionsWithValueAsTimezoneId,
  getUniqueIdFromTitle
} from '../MentorAvailabilityPage/MentorAvailabilityPageStyles'
import { GoogleCalendarIntegrationComponent } from '../MentorCalendarIntegrationsPage/GoogleCalendarIntegration'
import {
  COButton,
  COButtonsContainer,
  COCalendarControlsContainer,
  COCreateURLSection,
  COInfoImage,
  COInfoSectionTextPart,
  COInfoText,
  COURLSectionInput,
  COURLSectionInputParent,
  COURLSectionSpan,
  COURLSectionText,
  COURLSectionTextValidation,
  CalendarOnboardingContainer,
  CalendarOnboardingInfoSection,
  CalendarOnboardingPageContainer,
  PositionedSlugValidationStatus,
  SlugElementsParent,
  StyledSelectTZCO
} from './MentorCalendarPageStyles'

export async function checkIfSlugValid(newSlug, mentorProfileData) {
  if (Boolean(newSlug)) {
    if (newSlug === mentorProfileData?.slug) {
      return { valid: true, unavailable: false }
    }
    const pattern = /^[a-z\-]+$/ // match only a-z and hyphen
    const matchesPattern = pattern.test(newSlug)
    if (matchesPattern) {
      const response = await getMPDataWithoutParams(
        `/api/secure/creator/slug/check-availability?slug=${newSlug}`,
      ) //fire api to check if input is unique
      if (response?.status === 200) {
        if (response?.data) {
          return { valid: true, unavailable: false }
        } else {
          return { valid: false, unavailable: true }
        }
      } else {
        toast.error('Error setting URL')
      }
    } else {
      return { valid: false, unavailable: false }
    }
  } else {
    return { valid: false, unavailable: false }
  }
}

export const CalendarOnboardingIntroductionComponent = () => {
  const debouncedCheckSlugValid = useCallback(
    debounce(async (newSlug) => {
      const validationObject = await checkIfSlugValid(newSlug, mentorProfileData)
      setIsSlugValid(validationObject?.valid)
      setSlugValidationLoading(false)
      setSlugUnavailable(validationObject?.unavailable)
    }, 500), // Adjust the delay (in milliseconds) as per your preference
    [], // Empty dependency array because the debounced function doesn't depend on props or state
  )
  const { mentorProfileData } = useContext(MentorProfileContext)

  const [activePageId, setActivePageId] = useState('introduction')

  const [slug, setSlug] = useState(mentorProfileData?.slug)
  const [isSlugValid, setIsSlugValid] = useState(false)
  const [slugValidationLoading, setSlugValidationLoading] = useState(false)
  const [slugUnavailable, setSlugUnavailable] = useState(false)

  const [oneOnOnePlans, setOneOnOnePlans] = useState([])

  const [selectedTimeZone, setSelectedTimeZone] = useState(
    mentorProfileData?.timeZone?.id ||
    timezones_list?.find((item) => item?.gmtvalue === getTimezoneOffset())?.timezone,
  )

  const [continueButtonLoading, setContinueButtonLoading] = useState(false)
  const [continueButtonEnabled, setContinueButtonEnabled] = useState(false)

  const [basicScheduleObject, setBasicScheduleObject] = useState({
    ...NEW_MENTOR_SCHEDULE,
    timeZone: selectedTimeZone,
  })

  const cleanedAppUrl = APP_CONSTANTS.LIVE_APP_URL.replace(/^https?:\/\//, '')

  function handleSlugChange(value) {
    setSlug(value)
    setSlugValidationLoading(true)
    debouncedCheckSlugValid(value)
  }

  async function getOneOnOnePlans() {
    const resp = await getMPData('/api/secure/teacher/1on1-plans')
    if (resp?.status === 200) {
      if (resp?.data) {
        setOneOnOnePlans(resp?.data)
      }
    }
  }

  // calendar schedule page :
  const handleCheckboxChange = (event, item) => {
    const { checked } = event.target

    setBasicScheduleObject((prevObject) => {
      const updatedObject = { ...prevObject }
      updatedObject.weeklyHourMap[item].available = checked
      return updatedObject
    })
  }

  async function submitFirstScheduleWithEvent(basicScheduleObject, selectedTimeZone) {
    const resp = await submitFirstScheduleWithEventFunction(
      basicScheduleObject,
      selectedTimeZone,
      oneOnOnePlans,
      {
        eventPostFunction: postMPData,
        eventPostUrl: '/api/secure/teacher/update/events',
        getEntityProfileFunction: getMPData,
        getEntityProfileUrl: '/api/secure/teacher/profile',
        schedulePostFunction: postMPData,
        schedulePostUrl: '/api/secure/teacher/update/schedule',
      },
    )

    return resp
  }

  // buttons next and skip :
  function handleSetupLater() {
    window.location.reload()
  }

  async function handleNext() {
    setContinueButtonLoading(true)
    const selectedTimeZoneData = timezones_list?.find((item) => item?.timezone === selectedTimeZone)
    if (activePageId === 'introduction') {
      if (
        slug !== mentorProfileData?.slug ||
        !Boolean(mentorProfileData?.timeZone) ||
        mentorProfileData?.timeZone?.id !== selectedTimeZone
      ) {
        const profilePayload = {
          ...mentorProfileData,
          slug: slug,
          timeZone: {
            id: selectedTimeZoneData?.timezone,
            offset: encodeURIComponent(selectedTimeZoneData?.gmtvalue),
            title: selectedTimeZoneData?.timezone,
          },
        }
        const response = await postMPDataWithoutParams('/api/secure/teacher/save', profilePayload)
        if (response?.status !== 200) {
          setContinueButtonLoading(false)
          toast.error('Error saving')
          return null
        }
      }
      setContinueButtonLoading(false)
      setActivePageId('general_availability')
    } else if (activePageId === 'general_availability') {
      const response = await submitFirstScheduleWithEvent(
        basicScheduleObject,
        encodeURIComponent(selectedTimeZoneData?.gmtvalue),
      )
      if (response?.status !== 200) {
        setContinueButtonLoading(false)
        toast.error('Error saving schedule and event')
        return null
      }
      setContinueButtonLoading(false)
      setActivePageId('connect_calendar')
    }
  }

  // side effects :
  useEffect(() => {
    if (activePageId === 'introduction') {
      if (mentorProfileData?.slug) {
        setIsSlugValid(true)
      }
    }
  }, [])

  useEffect(() => {
    getOneOnOnePlans()
  }, [])

  useEffect(() => {
    function handleContinueValidation() {
      if (activePageId === 'introduction') {
        if (Boolean(slug) && isSlugValid && !slugValidationLoading && selectedTimeZone) {
          return true
        } else {
          return false
        }
      } else if (activePageId === 'general_availability') {
        return true
      }
    }
    setContinueButtonEnabled(handleContinueValidation())
  }, [slug, isSlugValid, slugValidationLoading, activePageId, selectedTimeZone]) // for validation of continue button

  return (
    <CalendarOnboardingPageContainer>
      {activePageId === 'introduction' && (
        <CalendarOnboardingContainer>
          <CalendarOnboardingInfoSection>
            <COInfoSectionTextPart>
              <COInfoText main>Welcome to AUMHUM Live!</COInfoText>
              <COInfoText>
                We take the work out of connecting with others so you can accomplish more.
              </COInfoText>
            </COInfoSectionTextPart>
            <COInfoImage src={ColorLinesIllustration} />
          </CalendarOnboardingInfoSection>

          <COCreateURLSection>
            <COURLSectionText main>Create your AUMHUM URL*</COURLSectionText>
            <COURLSectionText>
              Choose a URL that describes you or your business in a concise way. Make it short and
              easy to remember so you can share links with ease.
            </COURLSectionText>
            <COURLSectionText style={{ fontStyle: 'itatic' }}>
              Your identifier should be lowercase alphabetic (a-z) and may include hyphens(-). It
              should not contain any special characters or spaces.
            </COURLSectionText>
            <SlugElementsParent>
              <COURLSectionSpan main>{`${cleanedAppUrl}/mentor/`}</COURLSectionSpan>
              <COURLSectionInputParent>
                <COURLSectionInput
                  spellCheck={false}
                  type="text"
                  value={slug}
                  onChange={(e) => handleSlugChange(e.target.value)}
                />
                <PositionedSlugValidationStatus>
                  {slugValidationLoading ? (
                    <Spinner animation="border" variant="dark" size="sm" />
                  ) : isSlugValid ? (
                    <CheckCircle sx={{ color: 'green', fontSize: '20px' }} />
                  ) : Boolean(slug) ? (
                    <Cancel sx={{ color: 'red', fontSize: '20px' }} />
                  ) : null}
                </PositionedSlugValidationStatus>
              </COURLSectionInputParent>
              {/* fire api to check if slug is available, lodash */}
            </SlugElementsParent>
            {slugUnavailable && (
              <COURLSectionTextValidation valid={false}>
                This URL is already taken.
              </COURLSectionTextValidation>
            )}
          </COCreateURLSection>

          <COInfoSectionTextPart>
            <COInfoText main>Time zone</COInfoText>
            <StyledSelectTZCO
              value={selectedTimeZone}
              onChange={(e) => setSelectedTimeZone(e.target.value)}
            >
              {getTimeZoneOptionsWithValueAsTimezoneId()}
            </StyledSelectTZCO>
          </COInfoSectionTextPart>
        </CalendarOnboardingContainer>
      )}

      {activePageId === 'general_availability' && (
        <>
          {/* <CalendarOnboardingContainer>
          <CalendarOnboardingInfoSection>
            <COInfoSectionTextPart>
              <COInfoText main>Set your availability</COInfoText>
              <COInfoText>
                Let AUMHUM know when you&apos;re typically available to accept meetings.
              </COInfoText>
              <COInfoText style={{ fontStyle: 'italic' }}>
                We will create your first event along with this step. You can customize both your
                events and your availability inside.
              </COInfoText>
            </COInfoSectionTextPart>
            <COInfoImage src={ColorLinesIllustration} />
          </CalendarOnboardingInfoSection>

          <COCalendarControlsContainer>
            <div>
              <GenericModalHeadlineLevelTwo>Available hours</GenericModalHeadlineLevelTwo>
              <GenericDisplayFlex sb style={{ width: '100%', gap: '5px' }}>
                <StyledSelectTZCO
                  value={basicScheduleObject?.startTime}
                  onChange={(e) =>
                    setBasicScheduleObject((prev) => ({
                      ...prev,
                      startTime: e.target.value,
                    }))
                  }
                >
                  {getSlotTimeOptions()}
                </StyledSelectTZCO>
                <span>to</span>
                <StyledSelectTZCO
                  value={basicScheduleObject?.endTime}
                  onChange={(e) =>
                    setBasicScheduleObject((prev) => ({
                      ...prev,
                      endTime: e.target.value,
                    }))
                  }
                >
                  {getSlotTimeOptions()}
                </StyledSelectTZCO>
              </GenericDisplayFlex>
              {basicScheduleObject?.startTime >= basicScheduleObject?.endTime && (
                <GenericModalHeadlineLevelTwo style={{ color: 'red' }}>
                  End time must be greater than Start time
                </GenericModalHeadlineLevelTwo>
              )}
            </div>

            <div>
              <GenericModalHeadlineLevelTwo>Available days</GenericModalHeadlineLevelTwo>
              <ModalDaysContainer>
                {RENDERING_ORDER_OF_DAYS?.map((item, idx) => {
                  return (
                    <GenericDisplayFlexColumn aic key={idx}>
                      <Checkbox
                        size="small"
                        sx={{
                          padding: 0,
                          '&.Mui-checked': {
                            color: 'blue',
                          },
                        }}
                        checked={basicScheduleObject?.weeklyHourMap?.[item]?.available}
                        onChange={(event) => handleCheckboxChange(event, item)}
                      />
                      <span>{DISPLAY_NAMES_OF_WEEKDAYS?.[item]?.shortName}</span>
                    </GenericDisplayFlexColumn>
                  )
                })}
              </ModalDaysContainer>
            </div>
          </COCalendarControlsContainer>
        </CalendarOnboardingContainer> */}
          <GeneralAvailabilityComponent
            initialSchedule={basicScheduleObject}
            setInitialSchedule={setBasicScheduleObject}
            handleDayCheckBox={handleCheckboxChange}
          />
        </>
      )}

      {activePageId === 'connect_calendar' && (
        <CalendarOnboardingContainer>
          <CalendarOnboardingInfoSection>
            <COInfoSectionTextPart>
              <COInfoText main>Connect your calendar</COInfoText>
              <COInfoText>
                Connect your calendar to add new events as they are scheduled.
              </COInfoText>
            </COInfoSectionTextPart>
            <COInfoImage src={ColorLinesIllustration} />
          </CalendarOnboardingInfoSection>

          <GoogleCalendarIntegrationComponent />
        </CalendarOnboardingContainer>
      )}

      <COButtonsContainer>
        {activePageId === 'connect_calendar' && (
          <button onClick={handleSetupLater}>Set up later</button>
        )}
        {(activePageId === 'introduction' || activePageId === 'general_availability') && (
          <COButton
            disabled={!continueButtonEnabled || continueButtonLoading}
            continueButtonLoading={continueButtonLoading}
            onClick={handleNext}
          >
            Continue
            {continueButtonLoading && <Spinner animation="border" variant="light" size="sm" />}
          </COButton>
        )}
      </COButtonsContainer>
    </CalendarOnboardingPageContainer>
  )
}

export function GeneralAvailabilityComponent({
  initialSchedule: basicScheduleObject,
  setInitialSchedule: setBasicScheduleObject,
  showHeading = true,
}) {
  const handleCheckboxChange = (event, item) => {
    const { checked } = event.target

    setBasicScheduleObject((prevObject) => {
      const updatedObject = { ...prevObject }
      updatedObject.weeklyHourMap[item].available = checked
      return updatedObject
    })
  }
  return (
    <CalendarOnboardingContainer>
      {showHeading && (
        <CalendarOnboardingInfoSection>
          <COInfoSectionTextPart>
            <COInfoText main>Set your availability</COInfoText>
            <COInfoText>
              Let AUMHUM know when you&apos;re typically available to accept meetings.
            </COInfoText>
            <COInfoText style={{ fontStyle: 'italic' }}>
              We will create your first event along with this step. You can customize both your
              events and your availability inside.
            </COInfoText>
          </COInfoSectionTextPart>
          <COInfoImage src={ColorLinesIllustration} />
        </CalendarOnboardingInfoSection>
      )}

      <COCalendarControlsContainer>
        <div>
          <GenericModalHeadlineLevelTwo>Available hours</GenericModalHeadlineLevelTwo>
          <GenericDisplayFlex sb style={{ width: '100%', gap: '5px' }}>
            <StyledSelectTZCO
              value={basicScheduleObject?.startTime}
              onChange={(e) =>
                setBasicScheduleObject((prev) => ({
                  ...prev,
                  startTime: e.target.value,
                }))
              }
            >
              {getSlotTimeOptions()}
            </StyledSelectTZCO>
            <span>to</span>
            <StyledSelectTZCO
              value={basicScheduleObject?.endTime}
              onChange={(e) =>
                setBasicScheduleObject((prev) => ({
                  ...prev,
                  endTime: e.target.value,
                }))
              }
            >
              {getSlotTimeOptions()}
            </StyledSelectTZCO>
          </GenericDisplayFlex>
          {basicScheduleObject?.startTime >= basicScheduleObject?.endTime && (
            <GenericModalHeadlineLevelTwo style={{ color: 'red' }}>
              End time must be greater than Start time
            </GenericModalHeadlineLevelTwo>
          )}
        </div>

        <div>
          <GenericModalHeadlineLevelTwo>Available days</GenericModalHeadlineLevelTwo>
          <ModalDaysContainer>
            {RENDERING_ORDER_OF_DAYS?.map((item, idx) => {
              return (
                <GenericDisplayFlexColumn aic key={idx}>
                  <Checkbox
                    size="small"
                    sx={{
                      padding: 0,
                      '&.Mui-checked': {
                        color: 'blue',
                      },
                    }}
                    checked={basicScheduleObject?.weeklyHourMap?.[item]?.available}
                    onChange={(event) => handleCheckboxChange(event, item)}
                  />
                  <span>{DISPLAY_NAMES_OF_WEEKDAYS?.[item]?.shortName}</span>
                </GenericDisplayFlexColumn>
              )
            })}
          </ModalDaysContainer>
        </div>
      </COCalendarControlsContainer>
    </CalendarOnboardingContainer>
  )
}

export async function submitFirstScheduleWithEventFunction(
  basicScheduleObject,
  selectedTimeZone,
  oneOnOnePlans,
  {
    eventPostFunction,
    eventPostUrl,
    schedulePostFunction,
    schedulePostUrl,
    getEntityProfileFunction,
    getEntityProfileUrl,
  },
  initialProfileData,
) {
  try {
    // first event array
    var responseProfile = initialProfileData

    if (!!eventPostFunction && !!eventPostUrl) {
      const subscriptionPlanIds = !!oneOnOnePlans
        ? { subscriptionPlanId: oneOnOnePlans?.[0]?.id }
        : { free: true }
      const firstEventTypesArray = [
        {
          ...EVENT_TEMPLATE_DATA,
          title: '30 Min Consultation',
          ...subscriptionPlanIds,
        },
      ]

      // fire event submission api. submit it.
      const responseEventSubmission = await eventPostFunction(eventPostUrl, firstEventTypesArray)
      if (responseEventSubmission?.status !== 200) {
        toast.error('Error saving events')
        throw new Error('Error saving events')
      }

      // get its id by calling profile again. get id from eventtypes array first item.
      if (!!getEntityProfileFunction && !!getEntityProfileUrl) {
        const response = await getEntityProfileFunction(getEntityProfileUrl)
        if (response?.status !== 200) {
          toast.error('Error getting profile')
          throw new Error('Error getting profile')
        } else {
          responseProfile = response.data
        }
        console.log(response)
      }
    }

    // prepare schedules array first item
    const data = addSlotTimeToData(basicScheduleObject)
    const firstScheduleData = {}
    firstScheduleData.defaultSchedule = true
    firstScheduleData.exemptDates = []
    firstScheduleData.id = getUniqueIdFromTitle(data?.title)
    firstScheduleData.title = data?.title
    firstScheduleData.weeklyHourMap = data?.weeklyHourMap
    firstScheduleData.timeZone = selectedTimeZone
    firstScheduleData.eventIds = responseProfile?.eventTypes?.[0]?.uid
      ? [responseProfile?.eventTypes?.[0]?.uid]
      : []

    // fire api to submit schedule along with the event id of the event
    const response = await schedulePostFunction(schedulePostUrl, [firstScheduleData])
    if (response?.status !== 200) {
      throw new Error('Error saving schedules')
    }

    return { status: 200, data: response.data }
  } catch (error) {
    console.log(error)
    return { status: 500, message: error.message }
  }
}
